import i18next, { t } from "i18next";
import type { JSX } from "react";
import { PlanStatusActive } from "@/common/enums/enums";
import { planDisplayName } from "@/lib/plan-name";
import { useAuthStore } from "@/store/auth.store";
import PlanStatusBadge from "./PlanStatusBadge";

// "Your plan will renew on …" / "… expire on …", or no subscription.
export const subscriptionEndText = (
  userData: ReturnType<typeof useAuthStore.getState>["userData"],
): string => {
  if (!userData?.periodEnd) {
    return t("settings.subscription.noSubscriptionAvailable");
  }
  // The interface language's date ("31 de dezembro de 2026"); English keeps
  // "December 31, 2026".
  const language = i18next.resolvedLanguage || i18next.language || "en";
  const subscriptionEndDate = new Date(
    userData.periodEnd * 1000,
  ).toLocaleDateString(language, {
    month: "long",
    day: language.startsWith("en") ? "2-digit" : "numeric",
    year: "numeric",
  });
  if (userData?.periodEndCancel) {
    return `${t("settings.subscription.yourPlanWillExpire")} ${subscriptionEndDate}`;
  }
  return `${t("settings.subscription.yourPlanWillRenew")} ${subscriptionEndDate}`;
};

export default function SubscriptionTopBar({
  shouldShowExpirationDate = true,
}: {
  shouldShowExpirationDate?: boolean;
}): JSX.Element {
  const { userData } = useAuthStore.getState();

  const getSubscriptionEndDate = (): string => subscriptionEndText(userData);

  return (
    <header className="bg-linear-to-r bg-grey-100 rounded-b-[12px]">
      <div className="py-1 px-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mx-auto max-w-5xl md:px-6">
          <div className="flex flex-wrap items-center gap-3 py-1 my-2">
            <div className="flex items-center gap-2">
              <span className="font-bold">
                {t("settings.subscription.subscription")}
              </span>
              <span className="text-sm font-light">
                {planDisplayName(userData?.planName)}
              </span>
              <PlanStatusBadge
                status={userData?.planStatus || PlanStatusActive.CANCELLED}
              />
            </div>
          </div>
          {shouldShowExpirationDate && (
            <span className="text-sm bg-white/10 px-2 rounded-full">
              {getSubscriptionEndDate()}
            </span>
          )}
        </div>
      </div>
    </header>
  );
}
